import React, {useEffect} from "react";
import EntityList from "../../components/list/EntityList";
import {useAuth} from "../../utils/hooks/useAuth";
import {useNavigate} from "react-router-dom";
import {useNotification} from "../../utils/hooks/useNotification";
import {useConfirm} from "material-ui-confirm";
import buildUrl from "../../utils/api/buildUrl";
import useSWR from "swr";
import {deleteFetcher, getFetcher} from "../../utils/api/fetchers";
import UserRole from "../../schemas/user/roles";
import {PaginatedSeasons} from "../../schemas/seasons/seasonsSchema";
import {Button, IconButton} from "@mui/material";
import InfoIcon from "@mui/icons-material/Info";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import useTableParams from "../../components/list/useTableParams";

const Seasons: React.FC = () => {
    const {hasPermission} = useAuth();
    const {sortKeys, sortDirections, page, rowsPerPage, filters, setPage, setSort} = useTableParams();
    const navigate = useNavigate();
    const {showNotification} = useNotification();
    const confirm = useConfirm();

    const url = buildUrl('/seasons', {
        page,
        size: rowsPerPage,
        sortBy: sortKeys,
        sortDirections,
        filters
    });
    const {data, mutate} = useSWR<PaginatedSeasons>(url, getFetcher, {keepPreviousData: true});
    const editPermission = hasPermission(UserRole.COMMODORE);

    useEffect(() => {
        setSort('year', 'desc');
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const handleCreateSeason = () => {
        navigate('/seasons/create');
    }

    const handleDetails = (seasonId: number) => {
        navigate(`/seasons/${seasonId}`);
    }

    const handleDelete = (seasonId: number) => {
        confirm({
            description: `Opravdu chcete smazat tuto sezónu?`,
            title: "Smazat sezónu",
            confirmationText: "Smazat",
            cancellationText: "Zrušit",
            confirmationButtonProps: {variant: "contained", color: "error"},
        })
            .then(() => {
                deleteFetcher(`/seasons/${seasonId}`).then(() => {
                    showNotification('Sezóna byla smazána', 'success');
                    if (data?.content.length === 1 && data.page.number > 0) {
                        setPage(data.page.number - 1);
                    } else {
                        mutate();
                    }
                }).catch(() => {
                    showNotification('Sezónu se nepodařilo smazat', 'error');
                });
            })
            .catch(() => {
            });
    }

    return (
        <EntityList
            title="Sezóny"
            controls={editPermission ? [
                <Button key="add" variant="contained" color="success" onClick={handleCreateSeason}>
                    Vytvořit sezónu
                </Button>
            ] : []}
            headers={[
                {
                    key: 'year',
                    label: 'Rok',
                    sortable: true,
                    filtered: true,
                    filterConfig: {type: 'number', min: 1900, max: 2100}
                },
                {
                    key: 'numberOfCountedRaces',
                    label: 'Počet započítaných závodů',
                    sortable: true,
                },
                {
                    key: 'controls', render: (season) => editPermission ? (
                        <>
                            <IconButton color="info" title="Detaily a úprava" onClick={() => handleDetails(season.id)}>
                                <EditIcon/>
                            </IconButton>
                            <IconButton color="error" title="Smazat" onClick={() => handleDelete(season.id)}>
                                <DeleteIcon/>
                            </IconButton>
                        </>
                    ) : (
                        <IconButton color="primary" onClick={() => handleDetails(season.id)}>
                            <InfoIcon/>
                        </IconButton>
                    )
                }
            ]}
            entities={data?.content ?? []}
            entitiesCount={data?.page.totalElements ?? 0}
            indexes
        />
    );
}

export default Seasons;